/**
 * Campo de fecha en dd/MM/aaaa.
 *
 * El `<input type="date">` nativo no sirve acá: el formato que muestra lo
 * decide el navegador según su idioma, no la página, así que la misma pantalla
 * se ve `dd/mm/aaaa` en una máquina y `mm/dd/yyyy` en otra. Con fechas del 1 al
 * 12 eso no se nota hasta que alguien procesa el día equivocado.
 *
 * Entonces se escribe en un campo de texto con máscara, y el calendario del
 * sistema queda disponible en el botón de al lado: un `<input type="date">`
 * oculto al que se le pide el selector. Se gana el formato garantizado sin
 * perder el calendario.
 *
 * Hacia afuera el valor sigue siendo ISO (`2026-09-11`), que es lo que viaja
 * a la API.
 */

import { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';

import { aDdMmAaaa, aIso, enmascararFecha } from '../lib/fechas';

export interface CampoFechaProps {
  readonly etiqueta: string;
  /** Fecha en ISO `YYYY-MM-DD`. */
  readonly value: string;
  readonly onChange: (iso: string) => void;
  readonly required?: boolean;
}

/** `showPicker` es reciente; donde no esté, el botón no se muestra. */
type InputConPicker = HTMLInputElement & { showPicker?: () => void };

export function CampoFecha({
  etiqueta,
  value,
  onChange,
  required = false,
}: CampoFechaProps): React.JSX.Element {
  const [texto, setTexto] = useState(() => (value ? aDdMmAaaa(value) : ''));
  const oculto = useRef<InputConPicker>(null);

  // El valor puede cambiar desde afuera (el calendario, o un reset del
  // formulario). Mientras se está tipeando no se pisa lo escrito: solo se
  // sincroniza cuando el texto ya no representa al valor vigente.
  useEffect(() => {
    if (aIso(texto) !== value) setTexto(value ? aDdMmAaaa(value) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const escribir = (crudo: string): void => {
    const enmascarado = enmascararFecha(crudo);
    setTexto(enmascarado);
    const iso = aIso(enmascarado);
    if (iso) onChange(iso);
  };

  // Al salir del campo se vuelve a la última fecha válida: dejar `11/0` a la
  // vista sugiere que el filtro dice algo que no dice.
  const salir = (): void => {
    if (aIso(texto) === null) setTexto(value ? aDdMmAaaa(value) : '');
  };

  return (
    <label className="campo campo--fecha">
      {etiqueta}
      <span className="campo-fecha">
        <input
          type="text"
          inputMode="numeric"
          placeholder="dd/mm/aaaa"
          maxLength={10}
          required={required}
          value={texto}
          onChange={(ev) => escribir(ev.target.value)}
          onBlur={salir}
        />
        <button
          type="button"
          className="campo-fecha__boton"
          aria-label={`Elegir ${etiqueta.toLowerCase()} en el calendario`}
          onClick={() => oculto.current?.showPicker?.()}
        >
          <Calendar size={15} aria-hidden="true" />
        </button>
        {/*
          Solo aporta el calendario del sistema. No recibe foco ni entra en la
          navegación por teclado: el campo que se lee y se edita es el de texto.
        */}
        <input
          ref={oculto}
          type="date"
          className="campo-fecha__nativo"
          tabIndex={-1}
          aria-hidden="true"
          value={value}
          onChange={(ev) => {
            onChange(ev.target.value);
            setTexto(ev.target.value ? aDdMmAaaa(ev.target.value) : '');
          }}
        />
      </span>
    </label>
  );
}
